import React, { useState, useEffect } from 'react';
import { getProviderImage } from '@/services/providerService';
import { Skeleton } from '@/components/ui/skeleton';
import { ImageIcon } from 'lucide-react';

interface ProviderImageProps {
  providerId: number;
  filename: string;
  alt: string;
  className?: string;
}

const ProviderImage: React.FC<ProviderImageProps> = ({ providerId, filename, alt, className = '' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  useEffect(() => {
    const loadImage = async () => {
      if (!filename) {
        setError(true);
        setLoading(false);
        return;
      }

      // If it's already a full URL, use it directly
      if (filename.startsWith('http')) {
        setImageSrc(filename);
        setLoading(false);
        return;
      }

      try {
        const result = await getProviderImage(providerId, filename);
        
        // Check if result exists and has the required properties
        if (result && result.mimetype && result.base64Data) {
          const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
          setImageSrc(imageUrl);
        } else {
          // Handle case where result is null or missing properties
          console.error('Invalid provider image data received:', result);
          setError(true);
        }
      } catch (err) {
        console.error('Error loading provider image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [providerId, filename]);

  // Rest of the component remains the same
  if (loading) {
    return <Skeleton className={`${className} rounded-md`} />;
  }

  if (error || !imageSrc) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 rounded-md`}>
        <ImageIcon className="text-gray-400 h-8 w-8" />
      </div>
    );
  }

  return (
    <img 
      src={imageSrc} 
      alt={alt} 
      className={`${className} object-cover rounded-md`} 
      onError={() => setError(true)}
    />
  );
};

export default ProviderImage;